//================================================================
//  Component: Form Renderar
//================================================================

//  Purpose: This is the standard component for rendering a digital request form in the My Requests or My Approvals page

//  Properties:
//    - task = {Object, this is the task object}

//  Example:
//    <FormRenderer
//      task={task}
//    ></FormRenderer>    

//================================================================

//Libraries
import React, { useContext, useEffect, useState } from 'react';

//Contexts
import { GetUser, SetAppErrors, GetAppSize } from '../../Library/GlobalContexts';

//Components
import TabView from '../TabView/TabView';
import TabViewM from '../TabViewM/TabViewM';
import PageComponent from '../PageComponent/PageComponent';

// - Digital Request Forms
import NewInitiative from '../DigitalRequestForms/NewInitiative';
import ContractRenewal from '../DigitalRequestForms/ContractRenewal';
import Enhancement from '../DigitalRequestForms/Enhancement';
import AdditionalLicense from '../DigitalRequestForms/AdditionalLicense';
import Decommission from '../DigitalRequestForms/Decommission';

// - Digital Commercial Request Forms
import EstablishNewAgreement from '../CommercialRequestForms/EstablishNewAgreement';
import VaryExtendRenewExistingAgreement from '../CommercialRequestForms/VaryExtendRenewExistingAgreement';
import TerminateExistingAgreement from '../CommercialRequestForms/TerminateExistingAgreement';
import AssignNovateAgreement from '../CommercialRequestForms/AssignNovateAgreement';
import EstablishNDA from '../CommercialRequestForms/EstablishNDA';

// - Demand Assessment Forms
import NewInitiativeForm from '../DemandAssessmentForms/NewInitiativeForm';
import ContractRenewalForm from '../DemandAssessmentForms/ContractRenewalForm';
import EnhancementForm from '../DemandAssessmentForms/EnhancementForm';
import AdditionalLicensesOrOveragesForm from '../DemandAssessmentForms/AdditionalLicensesOrOveragesForm';
import DecommissionForm from '../DemandAssessmentForms/DecommissionForm';

//Functions
import QueryListener from '../../Library/QueryListener';
import TotalContractValue from '../TotalContractValue/TotalContractValue';

//Images

//CSS


export default function FormRenderer({ 
    task 
}) {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const setAppErrors = useContext(SetAppErrors);
    const getAppSize = useContext(GetAppSize);

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to determine status of the page > 'pending', 'onload', 'error-fatal'
    const [pageStatus, setPageStatus] = useState('pending');

    // Contains Firestore Document of the request
    const [requestDoc, setRequestDoc] = useState(undefined);

    // Additional Information Task Types
    const taskTypeForAdditionalInformation = ['Finance Approval', 'LoA Approval', 'COO Approval', 'Final Approval'];

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Use the request type and formtype to determine what form to show (request form or demand assessment form)
    const renderForm = (formType) => {
        
        switch (requestDoc?.requesttype) {

            // Digital Request Forms
            case 'New Initiative or Technology':
                return formType === 'Digital Request' ? <NewInitiative task={requestDoc}></NewInitiative> : <NewInitiativeForm collectionid={'requests'} documentid={requestDoc?.requestid} task={requestDoc}>
                </NewInitiativeForm>;
            case 'Contract Renewal':
                return formType === 'Digital Request' ? <ContractRenewal task={requestDoc}></ContractRenewal> : <ContractRenewalForm collectionid={'requests'} documentid={requestDoc?.requestid} task={requestDoc}>
                </ContractRenewalForm>;
            case 'Enhancement':
                return formType === 'Digital Request' ? <Enhancement task={requestDoc}></Enhancement> : <EnhancementForm collectionid={'requests'} documentid={requestDoc?.requestid} task={requestDoc}>
                </EnhancementForm>;
            case 'Additional Licenses or Overages':
                return formType === 'Digital Request' ? <AdditionalLicense task={requestDoc}></AdditionalLicense> : <AdditionalLicensesOrOveragesForm collectionid={'requests'} documentid={requestDoc?.requestid} task={requestDoc}>
                </AdditionalLicensesOrOveragesForm>;
            case 'Decommission':
                return formType === 'Digital Request' ? <Decommission task={requestDoc}></Decommission> : <DecommissionForm collectionid={'requests'} documentid={requestDoc?.requestid} task={requestDoc}>
                </DecommissionForm>;

            // Digital Commercial Request Forms
            case 'Establish New Agreement':
                return <EstablishNewAgreement task={requestDoc}></EstablishNewAgreement>;
            case 'Vary/Extend/Renew Existing Agreement':
                return <VaryExtendRenewExistingAgreement task={requestDoc}></VaryExtendRenewExistingAgreement>;
            case 'Terminate Existing Agreement':
                return <TerminateExistingAgreement task={requestDoc}></TerminateExistingAgreement>;
            case 'Assign/Novate Agreement':
                return <AssignNovateAgreement task={requestDoc}></AssignNovateAgreement>;
            case 'Establish NDA':
                return <EstablishNDA task={requestDoc}></EstablishNDA>;

            default:
                return <div>No form available for this request type.</div>;

        }
    };

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    useEffect(() => {

        function onLoadChange(documents) {

            if (documents && documents.length > 0) {
                // Get the first task document since we're querying for a specific parentrequestid
                const task = documents[0];
                setRequestDoc(task);

            } else {
                console.error('No task document found for the given parentrequestid');
                setPageStatus('error-fatal');
                return;

            }

            setPageStatus('onload');
        }

        function onError(error) {

            console.error('Error fetching task:', error);
            setPageStatus('error-fatal');
            setAppErrors(prevErrors => [...prevErrors, {
                message: 'Error loading request details',
                technical: error.message
            }]);

        }

        // Set up the query listener
        const unsubscribe = QueryListener('requests', [
                ['parentrequestid', '==', task?.parentrequestid],
                ['tasktype', 'in', ['Digital Request', 'Digital Commercial Request']]
            ], onLoadChange, onLoadChange, onError);

        // Cleanup function
        return () => {
            unsubscribe();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [task, getUser?.emailaddress, setAppErrors]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        getAppSize.width > 768 ? 

        // Desktop Version 
        <TabView
            defaultView={1}

            // DIGITAL REQUEST / DIGITAL COMMERCIAL REQUEST
            oneTitle={task?.requestorigin === 'Digital' ? 'Digital Commercial Request' : 'Digital Request'}
            oneContent={
                <PageComponent
                    status={pageStatus}
                    body={
                        <div className='py-0 px-3'>
                            {renderForm('Digital Request')}
                        </div>
                    }
                ></PageComponent>
            }

            // ADDITIONAL INFORMATION / QUOTE
            twoTitle={task?.requestorigin === 'Digital' ? 'Quote' : 'Additional Information'}
            twoContent={
                <PageComponent
                    status={pageStatus}
                    body={
                        task?.requestorigin === 'Digital' ?
                        <div>
                            <div className='border border-[#D8D8D8] rounded'>
                                <table className='w-full max-h-96 overflow-y-scroll'>
                                    <thead>
                                        <tr className='text-[#424242]'>
                                            <th className='py-[15px] px-[20px] font-medium'>Description</th>
                                            <th className='py-[15px] px-[10px] font-medium'>Quantity</th>
                                            <th className='py-[15px] px-[10px] font-medium'>Unit Price (exTax)</th>
                                            <th className='py-[15px] px-[10px] font-medium'>Total (exTax)</th>
                                            <th className='py-[15px] px-[10px] font-medium'>Currency</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            task?.quote?.length === 0 || task?.quote === undefined ?
                                                (
                                                    <tr>
                                                        <td className='border-t border-t-[#D8D8D8]' colSpan={5}>
                                                            <div className='text-center p-[2%]'>
                                                                Click on the <b>+</b> to add costs
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                                :
                                                (
                                                    <>
                                                        {
                                                            task?.quote?.map((item, index) => (
                                                                <tr key={index}>

                                                                    {/* Description */}
                                                                    <td className='py-[10px] px-[20px] border-t border-t-[#D8D8D8]'>
                                                                        <div className='max-w-[250px] leading-normal whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer'>
                                                                            {item?.description?.length > 0 ? item?.description : '-'}
                                                                        </div>
                                                                    </td>

                                                                    {/* Quantity */}
                                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                                        {item?.quantity}
                                                                    </td>

                                                                    {/* Unit Price (exTax) */}
                                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                                        {item?.unitprice}
                                                                    </td>

                                                                    {/* Total (exTax) */}
                                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                                        {item?.total}
                                                                    </td>

                                                                    {/* Currency */}
                                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                                        {item?.currency}
                                                                    </td>

                                                                </tr>
                                                            ))
                                                        }
                                                        <tr>
                                                            <td className='border-t border-t-[#D8D8D8] py-[10px] px-[20px] text-right' colSpan={5}>
                                                                Total Quote Value (exTax): <b>{(task.quote.reduce((sum, item) => sum + item.total, 0))?.toFixed(2)?.toLocaleString()} {task?.quote[0]?.currency}</b>
                                                            </td>
                                                        </tr>
                                                    </>
                                                )
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        :
                        <div className='py-2 px-3'>
                            {renderForm('Additional Information')}
                        </div>
                    }
                ></PageComponent>
            }
            twoHidden={(taskTypeForAdditionalInformation.includes(task?.tasktype) || task?.requestorigin === 'Digital') ? false : true}
            
            // TOTAL CONTRACT VALUE
            threeTitle={getAppSize.width > 768 ? 'Total Contract Value' : 'Contract Value'}
            threeContent={
                <PageComponent
                    status={pageStatus}
                    body={
                        <TotalContractValue
                            task={requestDoc}
                            view={'read'}
                        ></TotalContractValue>
                    }
                ></PageComponent>
            }
            threeHidden={taskTypeForAdditionalInformation.includes(task?.tasktype) ? false : true}
        ></TabView> 
        
        // Mobile Version 
        :
        <TabViewM
            defaultView={1}
            tabs={[
                {
                    id: 1,
                    title: task?.requestorigin === 'Digital' ? 'Commercial Request' : 'Digital Request',
                    content: <PageComponent
                        status={pageStatus}
                        body={
                            renderForm('Digital Request')
                        }
                    ></PageComponent>,
                    hidden: false
                },
                {
                    id: 2,
                    title: task?.requestorigin === 'Digital' ? 'Quote' : 'Additional Info',
                    content: <PageComponent
                        status={pageStatus}
                        body={
                            task?.requestorigin === 'Digital' ?
                            <div>
                                <div className='border border-[#D8D8D8] rounded overflow-x-auto'>
                                    <table className='w-full max-h-96 overflow-y-scroll '>
                                        <thead>
                                            <tr className='text-[#424242]'>
                                                <th className='py-[15px] px-[20px] font-medium'>Description</th>
                                                <th className='py-[15px] px-[10px] font-medium'>Quantity</th>
                                                <th className='py-[15px] px-[10px] font-medium'>Unit Price (exTax)</th>
                                                <th className='py-[15px] px-[10px] font-medium'>Total (exTax)</th>
                                                <th className='py-[15px] px-[10px] font-medium'>Currency</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                task?.quote?.length === 0 || task?.quote === undefined ?
                                                    (
                                                        <tr>
                                                            <td className='border-t border-t-[#D8D8D8]' colSpan={5}>
                                                                <div className='text-center p-[2%]'>
                                                                    Click on the <b>+</b> to add costs
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )
                                                    :
                                                    (
                                                        <>
                                                            {
                                                                task?.quote?.map((item, index) => (
                                                                    <tr key={index}>

                                                                        {/* Description */}
                                                                        <td className='py-[10px] px-[20px] border-t border-t-[#D8D8D8]'>
                                                                            <div className='max-w-[250px] leading-normal whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer'>
                                                                                {item?.description?.length > 0 ? item?.description : '-'}
                                                                            </div>
                                                                        </td>

                                                                        {/* Quantity */}
                                                                        <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                                            {item?.quantity}
                                                                        </td>

                                                                        {/* Unit Price (exTax) */}
                                                                        <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                                            {item?.unitprice}
                                                                        </td>

                                                                        {/* Total (exTax) */}
                                                                        <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                                            {item?.total}
                                                                        </td>

                                                                        {/* Currency */}
                                                                        <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                                            {item?.currency}
                                                                        </td>

                                                                    </tr>
                                                                ))
                                                            }
                                                            <tr>
                                                                <td className='border-t border-t-[#D8D8D8] py-[10px] px-[20px] text-right' colSpan={5}>
                                                                    Total Quote Value (exTax): <b>{(task.quote.reduce((sum, item) => sum + item.total, 0))?.toFixed(2)?.toLocaleString()} {task?.quote[0]?.currency}</b>
                                                                </td>
                                                            </tr>
                                                        </>
                                                    )
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            :
                            <div className='py-2 px-3'>
                                {renderForm('Additional Information')}
                            </div>
                        }
                    ></PageComponent>,
                    hidden: (taskTypeForAdditionalInformation.includes(task?.tasktype) || task?.requestorigin === 'Digital') ? false : true
                },
                {
                    id: 3,
                    title: 'TCV',
                    content: <PageComponent
                        status={pageStatus}
                        body={
                            <TotalContractValue
                                task={requestDoc}
                            ></TotalContractValue>
                        }
                    ></PageComponent>,
                    hidden: taskTypeForAdditionalInformation.includes(task?.tasktype) ? false : true
                }
            ]}>

        </TabViewM>
    )
}